/**
 * Phase 66 Plan 02 — auth event bus.
 *
 * Tiny EventTarget-based decoupling layer between `src/api/client.ts`
 * (which detects 401 responses) and `src/auth/AuthProvider.tsx` (which
 * needs to react by clearing state). Avoids a circular import between
 * the API module and the AuthProvider context.
 *
 * Events:
 *   - "logout" — fire-and-forget, no payload. AuthProvider listens and
 *     clears localStorage + user state on receipt.
 */

type AuthEvent = "logout";
type Listener = () => void;

const listeners = new Map<AuthEvent, Set<Listener>>();

export const authBus = {
  subscribe(event: AuthEvent, listener: Listener): () => void {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event)!.add(listener);
    return () => {
      listeners.get(event)?.delete(listener);
    };
  },

  dispatch(event: AuthEvent): void {
    const set = listeners.get(event);
    if (!set) return;
    for (const listener of set) {
      try {
        listener();
      } catch {
        // Swallow listener errors — bus must remain reliable.
      }
    }
  },
};
